import { useQuery } from '@tanstack/vue-query'
import { toValue } from 'vue'
import { useGqlMikro } from '~/composables/useGqlMikro'
import { useCollect } from '~/composables/useCollect'
import { COLLECTS_QUERY_LIST } from '~/queries/collects'
import { CollectStatus } from '~/types/graphql-backend-types/gql-types'

export const UNRATED_COLLECTS_QUERY_KEY = (selectedOrgId: MaybeRefOrGetter<string | undefined>) => ['unrated-collects', selectedOrgId]

export function useUnratedCollects() {
  const { query } = useGqlMikro()
  const { selectedOrgId } = storeToRefs(useOrganizationStore())
  const { isCompletedStatusForProducer } = useCollect()

  return useQuery({
    queryKey: UNRATED_COLLECTS_QUERY_KEY(selectedOrgId),
    queryFn: async () => {
      const orgId = toValue(selectedOrgId)
      const now = new Date()

      // Fetch completed collects (excluding HasIncident)
      const statusFilters = [
        CollectStatus.Treated,
        CollectStatus.Invoicing,
        CollectStatus.Finished,
      ]

      const { data, errors } = await query({
        query: COLLECTS_QUERY_LIST,
        variables: {
          pagination: { limit: 100, offset: 0 },
          filters: {
            status: statusFilters,
            ...(orgId && { organizationIds: [orgId] }),
            orderBy: { field: 'endsAt', order: 'DESC' as const },
          },
        },
      })

      if (errors && errors.length > 0) {
        throw errors
      }

      // Filter client-side: past collects without rating
      const unratedCollects = data.collects.collection.filter((collect) => {
        if (!collect) return false
        const isCompleted = isCompletedStatusForProducer(collect.status)
        const isPast = new Date(collect.endsAt).getTime() < now.getTime()
        const hasNoRating = !collect.npsRating?.rating
        return isCompleted && isPast && hasNoRating
      })

      return {
        collection: unratedCollects,
        count: unratedCollects.length,
      }
    },
  })
}
